// What a cited source actually says, read on hover. The chip names the
// source; this is the rule, the fact, or the record it points at, so a
// reader can check the claim without leaving the sentence.
import { type ReactNode } from 'react';

import { type ReferenceType } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import { useHarnessScope } from '@apps/web/features/phoebe_chat_v3/ui/harness_scope_context';
// The hooks barrel reaches the whole application, which the v3 import
// boundary forbids; the query primitive underneath it is allowed.
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';

const REASON = 'v3-source-citation-hover';

// The card shows a few lines of a record, not a rendered document, so
// heading and list markers would read as punctuation the agency typed.
const MARKDOWN_MARKERS = /^[ \t]*(?:#{1,6}[ \t]+|[-*+][ \t]+|>[ \t]?)/gmu;

function asPlainText(markdown: string): string {
    return markdown.replace(MARKDOWN_MARKERS, '').replace(/\*\*/gu, '').trim();
}

/** Loading, unreachable, and read are three different things to a reader,
 * and a nullable string can only tell them two of them apart. */
type PreviewState =
    | Readonly<{ readonly kind: 'loading' }>
    | Readonly<{ readonly kind: 'failed' }>
    | Readonly<{ readonly kind: 'ready'; readonly text: string }>;

function previewState(
    data: unknown,
    error: unknown,
    read: () => string,
): PreviewState {
    if (error !== undefined && error !== null) {
        return { kind: 'failed' };
    }
    if (data === undefined) {
        return { kind: 'loading' };
    }
    return { kind: 'ready', text: read() };
}

// The record's own words, set apart from the card's labels. Without the
// panel a reader cannot tell where our chrome stops and the agency's
// writing starts, which is the whole point of showing it.
function PreviewBody({ state }: Readonly<{ readonly state: PreviewState }>) {
    return (
        <span className="border-border/60 bg-muted/40 text-foreground/90 text-v3-chat-meta block max-h-28 overflow-hidden rounded border px-2 py-1.5">
            {state.kind === 'ready' ? (
                // No `block` here: line-clamp needs its own display value,
                // and a record can run to any length the agency wrote.
                <span className="line-clamp-6 whitespace-pre-line">
                    {asPlainText(state.text)}
                </span>
            ) : (
                <span className="text-muted-foreground">
                    {state.kind === 'loading'
                        ? 'Loading…'
                        : 'Could not open this record.'}
                </span>
            )}
        </span>
    );
}

function PlaybookPreview({ id }: Readonly<{ readonly id: string }>) {
    const scope = useHarnessScope();
    const organizationId = scope?.organizationId ?? null;
    const { data, error } = useApiQuery({
        path: '/organizations/{organization_id}/agent-rules/playbooks',
        method: 'get',
        pathParams:
            organizationId === null
                ? undefined
                : { organization_id: organizationId },
        enabled: organizationId !== null,
        select: (response) => response,
        reason: REASON,
        mode: scope?.mode,
    });
    return (
        <PreviewBody
            state={previewState(data, error, () => {
                const item = data?.items.find(
                    (candidate) => candidate.id === id,
                );
                if (item === undefined) {
                    return 'This rule is no longer in your playbook.';
                }
                return (
                    item.body_markdown.trim() ||
                    'Nothing is written in this playbook yet.'
                );
            })}
        />
    );
}

function CarePlanPreview({ id }: Readonly<{ readonly id: string }>) {
    const scope = useHarnessScope();
    const organizationId = scope?.organizationId ?? null;
    const { data, error } = useApiQuery({
        path: '/organizations/{organization_id}/clients/{client_id}',
        method: 'get',
        pathParams:
            organizationId === null
                ? undefined
                : { organization_id: organizationId, client_id: id },
        enabled: organizationId !== null,
        select: (response) => response,
        reason: REASON,
        mode: scope?.mode,
    });
    return (
        <PreviewBody
            state={previewState(data, error, () => {
                const summary = data?.care_plan_summary;
                return (
                    summary?.bullets.trim() ||
                    summary?.summary.trim() ||
                    data?.care_plan?.trim() ||
                    'No care plan recorded.'
                );
            })}
        />
    );
}

function CaregiverMemoryPreview({ id }: Readonly<{ readonly id: string }>) {
    const scope = useHarnessScope();
    const organizationId = scope?.organizationId ?? null;
    const { data, error } = useApiQuery({
        path: '/organizations/{organization_id}/caregivers/{caregiver_id}/memory',
        method: 'get',
        pathParams:
            organizationId === null
                ? undefined
                : { organization_id: organizationId, caregiver_id: id },
        enabled: organizationId !== null,
        select: (response) => response,
        reason: REASON,
        mode: scope?.mode,
    });
    return (
        <PreviewBody
            state={previewState(
                data,
                error,
                () =>
                    data?.compiled_markdown?.trim() || 'Nothing recorded yet.',
            )}
        />
    );
}

function ClientMemoryPreview({ id }: Readonly<{ readonly id: string }>) {
    const scope = useHarnessScope();
    const organizationId = scope?.organizationId ?? null;
    const { data, error } = useApiQuery({
        path: '/organizations/{organization_id}/clients/{client_id}/memory',
        method: 'get',
        pathParams:
            organizationId === null
                ? undefined
                : { organization_id: organizationId, client_id: id },
        enabled: organizationId !== null,
        select: (response) => response,
        reason: REASON,
        mode: scope?.mode,
    });
    return (
        <PreviewBody
            state={previewState(
                data,
                error,
                () =>
                    data?.compiled_markdown?.trim() || 'Nothing recorded yet.',
            )}
        />
    );
}

export function SourceCitationPreview({
    type,
    id,
}: Readonly<{
    readonly type: ReferenceType;
    readonly id: string;
}>): ReactNode {
    switch (type) {
        case 'playbook':
            return <PlaybookPreview id={id} />;
        case 'care_plan':
            return <CarePlanPreview id={id} />;
        case 'caregiver_memory':
            return <CaregiverMemoryPreview id={id} />;
        case 'client_memory':
            return <ClientMemoryPreview id={id} />;
        default:
            return null;
    }
}
